import Tooltip, { TailPlacement } from "@/components/design-system/tooltip";
import BoltIcon from "public/next-public/common/icons/bolt-solid.svg";
import React from "react";
import styles from "./badge.module.scss";

const ICON_SIZE: Record<BadgeSize, number> = {
  small: 12,
  medium: 16,
  large: 16,
};

function Badge({
  label,
  color = "gray",
  size = "small",
  strong = true,
  outline = false,
  showIcon = true,
  icon,
  className,
  tooltip,
  tooltipPlacement = "above",
}: BadgeProps) {
  const classNames = [
    styles.Badge,
    styles[`size-${size}`],
    styles[color],
    outline ? styles.outline : strong ? styles.strong : styles.subtle,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const iconSize = ICON_SIZE[size];
  const hasTooltip = Boolean(tooltip);

  const badge = (
    <span
      className={classNames}
      // The badge carries focus so keyboard users can reach the tooltip
      tabIndex={hasTooltip ? 0 : undefined}
    >
      {showIcon && (
        <span className={styles.iconSlot}>
          {icon ?? <BoltIcon className={styles.icon} width={iconSize} height={iconSize} aria-hidden />}
        </span>
      )}
      <span className={styles.label}>{label}</span>
    </span>
  );

  if (!hasTooltip) {
    return badge;
  }

  // Plain strings get the narrow, centered text bubble; anything else is rich
  // content and gets the wider, left-aligned layout.
  const tooltipProps = typeof tooltip === "string" ? { text: tooltip } : { content: tooltip };

  return (
    <Tooltip {...tooltipProps} tailPlacement={tooltipPlacement} anchorInline describeChild>
      {badge}
    </Tooltip>
  );
}

export default Badge;

export type BadgeColor = "gray" | "green" | "blue" | "red" | "orange" | "yellow" | "magenta" | "darkBlue";

export type BadgeSize = "small" | "medium" | "large";

interface BadgeProps {
  /** Text rendered inside the badge. */
  label: string;
  /** Design-system color. Defaults to "gray". */
  color?: BadgeColor;
  /** Size token — scales padding, font, and icon box together. Defaults to "small". */
  size?: BadgeSize;
  /** Filled (solid background, light text) when true; subtle (tinted background, dark text) when false. Defaults to true. */
  strong?: boolean;
  /** Bordered pill: transparent background with a 1px border and matching text. Overrides `strong`/subtle. */
  outline?: boolean;
  /** Render the leading icon. Defaults to true (lightning bolt). */
  showIcon?: boolean;
  /** Override the leading icon node. Falls back to the lightning bolt when omitted. */
  icon?: React.ReactNode;
  className?: string;
  /** Tooltip revealed on hover/focus of the badge. Omit for a plain badge. */
  tooltip?: React.ReactNode;
  /** Preferred side for the tooltip; flips automatically when there is no room. Defaults to "above". */
  tooltipPlacement?: TailPlacement;
}
